import { useControllableProp } from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ValueType } from 'react-select';

import { useTeamMemberList, useContactSearch } from '@frontend/api';
import {
  Box,
  Text,
  Avatar,
  Button,
  EmailSelect,
  Flex,
  SelectComponents,
  type CreatableSelectProps,
} from '@frontend/components';

import { sortByName } from 'modules/common/smartComponents/TeamMemberSelect/utils';

import { getGroupId } from 'store/session/selectors';

import { useDebounce } from 'utils/hooks';

type RecipientOption = {
  value: string;
  label: string;
  email: string;
  avatarColor?: string;
  isRequired?: boolean;
  handleLoadMore?: () => void;
};

type RecipientOptionGroup = {
  label: string;
  options: RecipientOption[];
};

type RecipientOptionComponentProps = SelectComponents.OptionProps<
  RecipientOption,
  true
>;

const RecipientOptionComponent = ({
  children: _children,
  ...restProps
}: RecipientOptionComponentProps) => {
  const { label, email, avatarColor, handleLoadMore } = restProps.data;

  if (handleLoadMore) {
    return (
      <Box ref={restProps.innerRef} px={2}>
        <Button
          size="md"
          variant="link"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleLoadMore();
          }}
        >
          {label}
        </Button>
      </Box>
    );
  }

  if (avatarColor) {
    return (
      <SelectComponents.Option {...restProps}>
        {avatarColor && (
          <Avatar
            alignSelf="center"
            marginY="auto"
            size="xs"
            marginRight={0.5}
            name={label}
            backgroundColor={avatarColor}
          />
        )}
        {label}
        {` (${email})`}
      </SelectComponents.Option>
    );
  }

  return (
    <SelectComponents.Option
      {...restProps}
      innerProps={{
        ...restProps.innerProps,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Text as="span" fontWeight="heavy">
        {label}
      </Text>
      {email && <Text as="span">{email}</Text>}
    </SelectComponents.Option>
  );
};

type RecipientMultiValueLabelProps =
  SelectComponents.MultiValueLabelProps<RecipientOption>;

const RecipientMultiValueLabel = ({
  data,
  ...restProps
}: RecipientMultiValueLabelProps) => (
  <SelectComponents.MultiValueLabel data={data} {...restProps}>
    <Flex alignItems="center" gap={0.5}>
      {data.avatarColor && (
        <Avatar
          alignSelf="center"
          size="xs"
          name={data.label}
          backgroundColor={data.avatarColor}
        />
      )}
      {data.label}
    </Flex>
  </SelectComponents.MultiValueLabel>
);

type RecipientMultiValueRemoveProps =
  SelectComponents.MultiValueRemoveProps<RecipientOption>;

const RecipientMultiValueRemove = ({
  data,
  ...restProps
}: RecipientMultiValueRemoveProps) => {
  if (data.isRequired) return null;
  return <SelectComponents.MultiValueRemove data={data} {...restProps} />;
};

const SELECT_COMPONENTS = {
  Option: RecipientOptionComponent,
  MultiValueLabel: RecipientMultiValueLabel,
  MultiValueRemove: RecipientMultiValueRemove,
};

export type MemberContactEmailSelectProps = Omit<
  CreatableSelectProps<RecipientOption, true>,
  'isMulti' | 'value' | 'onChange' | 'options' | 'inputValue' | 'onInputChange'
> & {
  locator?: string;
  accountId?: number;
  requiredEmail?: string;
  requiredEmailLabel?: string;
  value?: string[];
  defaultValue?: string[];
  onChange?: (emails: string[]) => void;
  contactsGroupLabel?: string; // to override account contacts option group label
};

export const MemberContactEmailSelect = ({
  locator: locatorProp,
  accountId,
  requiredEmail,
  requiredEmailLabel,
  value: valueProp,
  defaultValue,
  onChange,
  placeholder = 'Add recipients...',
  contactsGroupLabel = 'Contacts',
  components,
  ...restProps
}: MemberContactEmailSelectProps) => {
  const defaultLocator = useSelector(getGroupId);
  const locator = locatorProp !== undefined ? locatorProp : defaultLocator;

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 250);

  const [internalValue, setInternalValue] = useState<string[]>(
    valueProp ?? defaultValue ?? [],
  );
  const [, value] = useControllableProp(valueProp, internalValue);

  const { data: teamMembers = [], isFetching: isFetchingMembers } =
    useTeamMemberList(
      { locator: locator ?? '' },
      {
        query: { search: debouncedSearch },
        queryOptions: { enabled: !!locator, keepPreviousData: true },
      },
    );

  const {
    data: contactPages,
    isFetching: isFetchingContacts,
    fetchNextPage,
    hasNextPage,
  } = useContactSearch(
    {
      query: debouncedSearch ?? '',
      ...(accountId != null
        ? { filters: { accounts: { idIn: [accountId] } } }
        : {}),
    },
    { queryOptions: { enabled: accountId != null, keepPreviousData: true } },
  );

  const handleLoadMore = useCallback(() => {
    if (!isFetchingContacts) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingContacts]);

  const teamMemberOptions = useMemo(
    () =>
      teamMembers
        .toSorted(sortByName)
        .reduce<RecipientOption[]>((out, member, index, arr) => {
          if (
            arr.every(
              (other, otherIndex) =>
                other.userId !== member.userId || otherIndex === index,
            )
          ) {
            out.push({
              value: member.email,
              label: `${member.firstName} ${member.lastName}`,
              email: member.email,
              avatarColor: member.avatarColor,
            });
          }
          return out;
        }, []),
    [teamMembers],
  );

  const contactOptions = useMemo<RecipientOption[]>(() => {
    const options = (contactPages?.pages ?? [])
      .flatMap((page) => page.results)
      .reduce<RecipientOption[]>((out, result) => {
        if (result.data.email) {
          out.push({
            value: result.data.email,
            label: result.data.fullName || result.data.email,
            email: result.data.email,
          });
        }
        return out;
      }, []);

    if (hasNextPage) {
      options.push({
        value: '__show_more__',
        label: 'Show more contacts',
        email: '',
        handleLoadMore,
      });
    }

    return options;
  }, [contactPages, hasNextPage, handleLoadMore]);

  const requiredOption = useMemo<RecipientOption | undefined>(
    () =>
      requiredEmail
        ? {
            value: requiredEmail,
            label: requiredEmailLabel ?? requiredEmail,
            email: requiredEmail,
            isRequired: true,
          }
        : undefined,
    [requiredEmail, requiredEmailLabel],
  );

  const groupedOptions = useMemo<RecipientOptionGroup[]>(() => {
    const groups: RecipientOptionGroup[] = [
      {
        label: 'Team Members',
        options: teamMemberOptions,
      },
    ];
    if (accountId != null) {
      groups.unshift({ label: contactsGroupLabel, options: contactOptions });
    }
    return groups;
  }, [teamMemberOptions, contactOptions, accountId, contactsGroupLabel]);

  const knownOptionsCacheRef = useRef(new Map<string, RecipientOption>());
  for (const option of teamMemberOptions) {
    knownOptionsCacheRef.current.set(option.value, option);
  }
  for (const option of contactOptions) {
    if (!option.handleLoadMore) {
      knownOptionsCacheRef.current.set(option.value, option);
    }
  }

  const selectedOptions =
    value
      ?.map((email) => {
        // ensure a required email is not removable even when it's a known team member contact
        const isRequired = requiredOption?.value === email;
        const known = knownOptionsCacheRef.current.get(email);
        return known
          ? { ...known, isRequired: known.isRequired || isRequired }
          : { value: email, label: email, email, isRequired };
      })
      ?.toSorted((a, b) =>
        a.isRequired === b.isRequired ? 0 : a.isRequired ? -1 : 1,
      ) ?? [];

  const handleChange = useCallback(
    (newValue: ValueType<RecipientOption, true>) => {
      const arr = newValue ?? [];
      const guarded =
        requiredOption && !arr.some((o) => o.value === requiredOption.value)
          ? [requiredOption, ...arr]
          : arr;
      const emails = guarded.map((o) => o.email);
      setInternalValue(emails);
      onChange?.(emails);
    },
    [onChange, requiredOption],
  );

  const handleCreateOption = useCallback(
    (inputValue: string) => {
      const newEmails = [...(value ?? []), inputValue];
      const guarded =
        requiredOption && !newEmails.includes(requiredOption.email)
          ? [requiredOption.email, ...newEmails]
          : newEmails;
      setInternalValue(guarded);
      onChange?.(guarded);
    },
    [onChange, requiredOption, value],
  );

  return (
    <EmailSelect
      isMulti
      filterOption={null}
      options={groupedOptions}
      value={selectedOptions}
      inputValue={searchTerm}
      onChange={handleChange}
      onCreateOption={handleCreateOption}
      onInputChange={setSearchTerm}
      isOptionDisabled={(option) =>
        !!(option as RecipientOption).handleLoadMore
      }
      isLoading={isFetchingMembers || isFetchingContacts}
      placeholder={placeholder}
      components={{ ...SELECT_COMPONENTS, ...components }}
      {...restProps}
    />
  );
};
